<template>
  <UIModal v-model="isOpen" :index-override="100000">
    <template #header>
      <div class="tw:mx-auto tw:w-28 tw:h-28">
        <img :src="chest" />
      </div>
      <h6 class="tw:text-white tw:text-3xl tw:text-center">
        {{
          $t('coreYourReward', {
            position: leaderboardPosition
          })
        }}
      </h6>
    </template>
    <template #default>
      <div class="tw:px-4 tw:md:min-w-3xl">
        <div
          v-if="enrichedRewards.length > 0"
          class="tw:justify-items-center tw:gap-4 tw:grid tw:grid-cols-3 tw:sm:grid-cols-4 tw:md:grid-cols-5 tw:mx-auto tw:p-4"
        >
          <div v-for="(reward, index) in enrichedRewards" :key="index" class="tw:w-full">
            <ItemCard
              :amount="reward.amount"
              :is-nft="reward.isNFT"
              :token-address="reward.token"
              :hut-index="reward?.hutIndex"
              class="tw:w-full"
            />
          </div>
        </div>
        <div v-else class="tw:py-8 tw:text-white tw:text-center">{{ $t('coreNoRewards') }}</div>
        <div v-if="showClaimButton" class="tw:flex tw:justify-center tw:mt-6 tw:pb-4">
          <button class="btn-primary" @click="handleClaim">
            {{ $t('referralClaimButtonLabel') }}
          </button>
        </div>
      </div>
    </template>
  </UIModal>
</template>

<script lang="ts" setup>
import { LEADERBOARD_REWARDS } from '~/utils/constants';
import chest from './assets/chest.webp';
import ItemCard from './ItemCard.vue';

interface Reward {
  token: string;
  isNFT: boolean;
  amount: string;
  hutIndex?: number;
}

const {
  showClaimButton = true,
  rewards,
  leaderboardPosition
} = defineProps<{
  leaderboardPosition: number;
  rewards?: Reward[];
  showClaimButton?: boolean;
}>();

const emit = defineEmits<{
  claim: [];
}>();

const isOpen = defineModel<boolean>({ default: false });

const HUT_INDEX_MAP: Record<string, number> = {
  little: 0,
  medium: 1,
  larger: 2,
  great: 3,
  specious: 4
};

/**
 * Извлекает hutIndex из названия награды
 * @param rewardName - название награды из LEADERBOARD_REWARDS
 * @returns hutIndex (0-4) или undefined если это не хижина
 */
const getHutIndexFromRewardName = (rewardName: string): number | undefined => {
  const normalizedName = rewardName.toLowerCase();

  for (const [hutType, index] of Object.entries(HUT_INDEX_MAP)) {
    if (normalizedName.includes(hutType)) {
      return index;
    }
  }

  return undefined;
};

/**
 * Обогащает массив наград данными hutIndex на основе позиции в лидерборде.
 * Применяет hutIndex только к NFT наградам (хижинам) в порядке их следования.
 */
const enrichedRewards = computed<Reward[]>(() => {
  const baseRewards: Reward[] = rewards || [];

  // Проверяем валидность позиции
  if (leaderboardPosition < 1 || leaderboardPosition > 30) {
    return baseRewards;
  }

  // Получаем названия наград для текущей позиции
  const rewardNames = LEADERBOARD_REWARDS[leaderboardPosition];
  if (!rewardNames?.length) {
    return baseRewards;
  }

  // Извлекаем hutIndex для каждого названия хижины
  const hutIndices = rewardNames.map(getHutIndexFromRewardName).filter((index): index is number => index !== undefined);

  if (hutIndices.length === 0) {
    return baseRewards;
  }

  // Применяем hutIndex к NFT наградам по порядку
  let nftCounter = 0;

  return baseRewards.map((reward: Reward) => {
    if (reward.isNFT && nftCounter < hutIndices.length) {
      return {
        ...reward,
        hutIndex: hutIndices[nftCounter++]
      };
    }
    return reward;
  });
});

const handleClaim = () => {
  emit('claim');
};
</script>
